import { Texture } from "pixi.js";

const TEX_W = 256;
const TEX_H = 256;

// Horizontal falloff: smooth bell across the line's width.
// 0.5 (center) → 1.0 alpha, edges → 0.
// Use a quartic so the peak is broad and the edges feather quickly.
function horizontalEnvelope(u: number): number {
  const x = 2 * u - 1; // -1..1
  const v = 1 - x * x; // parabola, 1 at centre, 0 at edges
  return v * v; // sharper centre, softer wings
}

// Vertical envelope matching the Unity gradient stops:
//   (0, 0)        → 0
//   (0.588, 0.30) → 0.588
//   (1, 0.45)     → 1.0   ← plateau begins
//   (1, 0.55)     → 1.0   ← plateau ends
//   (0.588, 0.70) → 0.588
//   (0, 1)        → 0
// I.e. the line fades in over the top 30%, sits at full intensity in the
// middle 40%, fades out over the bottom 30%.
function verticalEnvelope(v: number): number {
  if (v <= 0 || v >= 1) return 0;
  if (v <= 0.30) return (v / 0.30) * 0.588;
  if (v <= 0.45) return 0.588 + ((v - 0.30) / 0.15) * (1 - 0.588);
  if (v <= 0.55) return 1.0;
  if (v <= 0.70) return 1 - ((v - 0.55) / 0.15) * (1 - 0.588);
  return 0.588 - ((v - 0.70) / 0.30) * 0.588;
}

function makeFalloffTexture(): Texture {
  const canvas = document.createElement("canvas");
  canvas.width = TEX_W;
  canvas.height = TEX_H;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(TEX_W, TEX_H);
  const data = img.data;
  for (let y = 0; y < TEX_H; y++) {
    const ev = verticalEnvelope((y + 0.5) / TEX_H);
    for (let x = 0; x < TEX_W; x++) {
      const eh = horizontalEnvelope((x + 0.5) / TEX_W);
      const a = Math.max(0, Math.min(1, eh * ev));
      const idx = (y * TEX_W + x) * 4;
      data[idx + 0] = 255;
      data[idx + 1] = 255;
      data[idx + 2] = 255;
      data[idx + 3] = Math.round(a * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return Texture.from(canvas);
}

export function createFalloffTexture(): Texture {
  return makeFalloffTexture();
}

// Vertical bell texture used for the focused-touch "spark" overlay.
// Cross-shape: square root of an elongated cos² so the bell decays slowly
// at the edges but has a clear bright core.
export function createSparkTexture(): Texture {
  const H = 256;
  const canvas = document.createElement("canvas");
  canvas.width = 8;
  canvas.height = H;
  const ctx = canvas.getContext("2d")!;
  const img = ctx.createImageData(8, H);
  for (let y = 0; y < H; y++) {
    const t = (y + 0.5) / H;
    const arg = (t - 0.5);                            // -0.5..0.5
    // cos⁴ centred bell — sharp peak, gentle wings.
    const c = Math.cos(Math.PI * arg);
    const bell = c * c * c * c;
    for (let x = 0; x < 8; x++) {
      const xu = (x + 0.5) / 8;
      const xc = 1 - Math.abs(2 * xu - 1);            // tent across width
      const tent = xc * xc;
      const a = Math.max(0, Math.min(1, bell * tent));
      const idx = (y * 8 + x) * 4;
      img.data[idx + 0] = 255;
      img.data[idx + 1] = 255;
      img.data[idx + 2] = 255;
      img.data[idx + 3] = Math.round(a * 255);
    }
  }
  ctx.putImageData(img, 0, 0);
  return Texture.from(canvas);
}
